import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  accountRequiredMessage,
  type ContractService,
  errors,
  paxRoute,
  unauthorizedMessage,
} from "../../common";
import { paymentsDependencyErrors } from "./errors";
import {
  AppStoreAccount,
  AppStoreAccountQuery,
  AppStoreTransactionBody,
  AppStoreTransactionOutcome,
  AutoTopUpRule,
  AutoTopUpSetupBody,
  AutoTopUpSetupResponse,
  BillingPortalSessionResponse,
  CheckoutCompletion,
  CheckoutSessionId,
  CheckoutSessionResponse,
  CreateCheckoutSessionBody,
  ListOffersResponse,
  SavedPaymentMethodPaymentBody,
  SavedPaymentMethodPaymentResponse,
  SavedPaymentMethodQuote,
  SavedPaymentMethodQuoteBody,
  SubscriptionResponse,
  TopUpSettings,
} from "./schemas";

export const service = {
  group: "payments",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Payments API",
  description:
    "Offers, checkout, App Store purchases, the caller's subscription, their saved payment method, and automatic top-up.",
} as const satisfies ContractService;

export const listOffers = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/payments/v1alpha1/offers",
      operationId: "payments.offers.list",
      summary: "List offers.",
      description:
        "Every offer a player can buy, in catalog order, with its price and what it grants.",
      successDescription: "The offer catalog.",
    }),
  )
  .output(ListOffersResponse);

const accountErrors = {
  UNAUTHORIZED: { message: unauthorizedMessage },
  FORBIDDEN: { message: accountRequiredMessage },
} as const;

export const createCheckoutSession = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/payments/v1alpha1/checkout-sessions",
      operationId: "payments.checkoutSessions.create",
      summary: "Create a checkout session.",
      description:
        "Starts Stripe Checkout for one offer and returns the URL to send the player to.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The Checkout Session.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "No offer has that id." },
    CONFLICT: { message: "You already have a live subscription of this kind." },
    ...paymentsDependencyErrors,
  })
  .input(z.object({ body: CreateCheckoutSessionBody }))
  .output(CheckoutSessionResponse);

export const completeCheckoutSession = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/payments/v1alpha1/checkout-sessions/{checkoutSessionId}/complete",
      operationId: "payments.checkoutSessions.complete",
      summary: "Complete a checkout session.",
      description:
        "Called when the player is back from Checkout. Reads the session from Stripe and grants a pack at once, even while a delayed payment clears; the webhook grants the same pack only once and removes it if that payment fails. A finished plan checkout answers `subscribed`, and the webhook grants its month.",
      inputStructure: "detailed",
      successDescription: "Whether the pack was granted or the plan bought.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "You have no checkout session with that id." },
    CONFLICT: { message: "This payment's credits could not be added. Contact support." },
    ...paymentsDependencyErrors,
  })
  .input(z.object({ params: z.object({ checkoutSessionId: CheckoutSessionId }) }))
  .output(CheckoutCompletion);

export const createBillingPortalSession = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/payments/v1alpha1/billing-portal-sessions",
      operationId: "payments.billingPortalSessions.create",
      summary: "Create a billing portal session.",
      description: "Returns the URL for the caller's Stripe customer portal.",
      successStatus: 201,
      successDescription: "The portal session.",
    }),
  )
  .errors({
    ...accountErrors,
    NOT_FOUND: { message: "This account has no Stripe customer yet." },
    ...paymentsDependencyErrors,
  })
  .output(BillingPortalSessionResponse);

export const getSubscription = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/payments/v1alpha1/subscription",
      operationId: "payments.subscription.get",
      summary: "Get my subscriptions.",
      description:
        "The caller's live token subscriptions and Patron subscriptions, one from each store they bought that kind in.",
      successDescription:
        "The caller's live token and Patron subscriptions, the one that runs longest first; each list is empty when the caller has none of that kind.",
    }),
  )
  .errors({
    ...accountErrors,
    ...paymentsDependencyErrors,
  })
  .output(SubscriptionResponse);

const noSavedPaymentMethod = {
  NOT_FOUND: { message: "This account has no saved payment method." },
} as const;

export const getTopUpSettings = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/payments/v1alpha1/top-up-settings",
      operationId: "payments.topUpSettings.get",
      summary: "Get my top-up settings.",
      description: "The caller's saved payment method and automatic top-up rule.",
      successDescription: "The caller's top-up settings.",
    }),
  )
  .errors({
    ...accountErrors,
    ...paymentsDependencyErrors,
  })
  .output(TopUpSettings);

export const setAutoTopUp = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/payments/v1alpha1/auto-top-up",
      operationId: "payments.autoTopUp.set",
      summary: "Set my automatic top-up.",
      description:
        "Sets or resumes the automatic top-up rule on the saved payment method: which pack to buy, and the balance that triggers it.",
      inputStructure: "detailed",
      successDescription: "The caller's top-up settings after the change.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...noSavedPaymentMethod,
    ...paymentsDependencyErrors,
  })
  .input(z.object({ body: AutoTopUpRule }))
  .output(TopUpSettings);

export const deleteAutoTopUp = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/payments/v1alpha1/auto-top-up",
      operationId: "payments.autoTopUp.delete",
      summary: "Turn off my automatic top-up.",
      description: "Turns the automatic top-up rule off. The saved payment method stays.",
      successDescription: "The caller's top-up settings after the change.",
    }),
  )
  .errors({
    ...accountErrors,
    ...paymentsDependencyErrors,
  })
  .output(TopUpSettings);

export const deleteSavedPaymentMethod = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/payments/v1alpha1/saved-payment-method",
      operationId: "payments.savedPaymentMethod.delete",
      summary: "Remove my saved payment method.",
      description:
        "Removes the saved payment method, which also turns the automatic top-up off. It does not cancel a subscription.",
      successDescription: "The caller's top-up settings after the change.",
    }),
  )
  .errors({
    ...accountErrors,
    ...noSavedPaymentMethod,
    ...paymentsDependencyErrors,
  })
  .output(TopUpSettings);

export const createAutoTopUpSetup = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/payments/v1alpha1/auto-top-up-setups",
      operationId: "payments.autoTopUpSetups.create",
      summary: "Create an automatic top-up setup.",
      description:
        "Starts Stripe Checkout in setup mode, where the player authorizes a payment method for automatic charges. The rule turns on when the setup completes.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The setup session's URL.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...paymentsDependencyErrors,
  })
  .input(z.object({ body: AutoTopUpSetupBody }))
  .output(AutoTopUpSetupResponse);

export const quoteSavedPaymentMethod = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/payments/v1alpha1/saved-payment-method/quote",
      operationId: "payments.savedPaymentMethod.quote",
      summary: "Quote a saved payment method purchase.",
      description: "Prices one offer on the saved payment method, with tax. Nothing is charged.",
      inputStructure: "detailed",
      successDescription: "The price with tax, or that Stripe needs a billing address first.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...noSavedPaymentMethod,
    ...paymentsDependencyErrors,
  })
  .input(z.object({ body: SavedPaymentMethodQuoteBody }))
  .output(SavedPaymentMethodQuote);

export const payWithSavedPaymentMethod = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/payments/v1alpha1/saved-payment-method/pay",
      operationId: "payments.savedPaymentMethod.pay",
      summary: "Pay with the saved payment method.",
      description:
        "Charges the saved payment method for one offer, at the total the player was shown. Sending the same `purchaseAttemptId` again never charges twice.",
      inputStructure: "detailed",
      successDescription: "What happened to the purchase.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...noSavedPaymentMethod,
    CONFLICT: {
      message:
        "A purchase is already in progress, a live subscription of this kind exists, or the payment's credits could not be added.",
    },
    ...paymentsDependencyErrors,
  })
  .input(z.object({ body: SavedPaymentMethodPaymentBody }))
  .output(SavedPaymentMethodPaymentResponse);

export const getAppStoreAccount = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/payments/v1alpha1/app-store-account",
      operationId: "payments.appStoreAccount.get",
      summary: "Get my App Store account.",
      description:
        "The token StoreKit tags the caller's purchases with, made on first read, whether they may buy in the App Store environment the app runs in, and whether their storefront lets the app offer Checkout on the web too.",
      inputStructure: "detailed",
      successDescription:
        "The caller's App Store account token, whether purchases are open, and whether web checkout is.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...paymentsDependencyErrors,
  })
  .input(z.object({ query: AppStoreAccountQuery }))
  .output(AppStoreAccount);

export const createAppStoreTransaction = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/payments/v1alpha1/app-store-transactions",
      operationId: "payments.appStoreTransactions.create",
      summary: "Report an App Store transaction.",
      description:
        "Verifies a transaction the App Store signed and grants what it bought to the player its account token names. Reporting it again, or after its App Store notification, grants nothing more.",
      inputStructure: "detailed",
      successDescription: "Whether the transaction's purchase is granted or was revoked.",
    }),
  )
  .errors({
    BAD_REQUEST: { message: "This isn't a transaction the App Store signed for Pax." },
    ...accountErrors,
    NOT_FOUND: { message: "This purchase belongs to a Pax account on another server." },
    CONFLICT: { message: "This payment's credits could not be added. Contact support." },
    ...paymentsDependencyErrors,
  })
  .input(z.object({ body: AppStoreTransactionBody }))
  .output(AppStoreTransactionOutcome);
